import { IonList } from "@ionic/react";
import { ChangeEvent, ReactNode, useMemo } from "react";
import { FieldError, FieldPath, FieldValues, Merge } from "react-hook-form";

import styles from "@components/fields/CheckboxGroup.module.css";
import Error from "@components/fields/Error";
import RequiredFieldIndicator from "@components/fields/RequiredFieldIndicator";
import Nbsp from "@components/Nbsp";
import InputLabel from "@components/typography/InputLabel";
import InputSubLabel from "@components/typography/InputSubLabel";

export type Option = {
  label: string;
  type: string;
  value: string;
};

type Props<FormData extends FieldValues> = {
  disabled?: boolean;
  errors: Merge<FieldError, (FieldError | undefined)[]> | undefined;
  inTracker?: boolean;
  label: string | ReactNode;
  name: FieldPath<FormData>;
  onChange: (values: Option[]) => void;
  options: Option[];
  required?: boolean;
  smaller?: boolean;
  smallerLabel?: boolean;
  subLabel?: string | ReactNode;
  values: Option[];
};

const CheckboxGroup = <FormData extends FieldValues>({
  disabled = false,
  errors,
  inTracker = false,
  label,
  name,
  onChange,
  options,
  required = false,
  smaller = false,
  subLabel,
  values
}: Props<FormData>) => {
  const arrayErrors = useMemo(() => {
    if (errors === undefined) {
      return undefined;
    }

    if (!Array.isArray(errors)) {
      return [errors];
    }
    return errors;
  }, [errors]);

  return (
    <div className={`${styles.checkboxGroup} ${inTracker ? "" : styles.inputWrapper}`}>
      <div>
        {label && (
          <div className={inTracker ? styles.trackerLabel : ""} id={`${name}-label`}>
            <InputLabel htmlFor={name}>
              {label}
              {required && <RequiredFieldIndicator />}
            </InputLabel>
            {subLabel && (
              <>
                <Nbsp />
                <InputSubLabel>{subLabel}</InputSubLabel>
              </>
            )}
          </div>
        )}
      </div>
      <div className={styles.controllerGroup}>
        <IonList
          aria-describedby={arrayErrors && arrayErrors.length > 0 ? `${name}-errors` : ""}
          aria-invalid={arrayErrors?.length ? "true" : "false"}
          aria-labelledby={label ? `${name}-label` : ""}
          aria-required={required}
          className={`${styles.list} ${inTracker ? styles.trackerList : ""} ${smaller ? styles.marginLeft : ""}`}
          data-test="checkbox-options"
          id={name}
        >
          {options.map(option => {
            const checked = values.some(o => o.value === option.value);
            const className = checked ? styles.checked : styles.notChecked;
            return (
              <div className={className} key={option.label} tabIndex={0}>
                <label>
                  <input
                    checked={checked}
                    className={styles.input}
                    disabled={disabled}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const castValue = values;
                      if (e.target.checked) {
                        onChange([...castValue, option]);
                      } else {
                        const newValue = [...castValue];
                        const index = newValue.indexOf(option);
                        newValue.splice(index, 1);
                        onChange(newValue);
                      }
                    }}
                    type="checkbox"
                    value={option.value}
                  />
                  {option.label}
                </label>
              </div>
            );
          })}
        </IonList>
      </div>
      {arrayErrors && arrayErrors.length > 0 && (
        <div className={inTracker ? "ion-text-center" : undefined} id={`${name}-errors`}>
          {arrayErrors?.map(error => <Error error={error} key={error?.message} />)}
        </div>
      )}
    </div>
  );
};

export default CheckboxGroup;
